import { createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/contexts/AuthContext";

interface IntelligenceContextType {
  intelligenceEnabled: boolean;
  isLoading: boolean;
  toggleIntelligence: () => void;
}

const IntelligenceContext = createContext<IntelligenceContextType | undefined>(undefined);

export function IntelligenceProvider({ children }: { children: React.ReactNode }) {
  // Fetch intelligence setting from app_settings with proper error handling
  // Asked only for someone signed in: the setting is staff-only, and a buyer
  // page (or the sign-in screen) got a 401 for it on every load.
  const { user } = useAuth();
  const { data: intelligenceSetting, isLoading } = useQuery({
    queryKey: ['/api/app-settings/salefishIntelligence'],
    enabled: !!user,
    queryFn: getQueryFn({ on401: "returnNull" }),
    // A platform switch a SaleFish Admin flips rarely, and the toggle
    // invalidates this key. It used to refetch on every focus (QA 2026-10-28).
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });

  // Extract setting value - handle PostgreSQL boolean strings properly
  // 't' = true, 'f' = false in PostgreSQL
  const settingValue = (intelligenceSetting as any)?.settingValue;
  
  // Check if value is explicitly enabled
  // If setting is 'f', false, or 'false' -> disabled
  // If setting is 't', true, or 'true' -> enabled
  // If setting is undefined/null and not loading -> default to enabled
  const intelligenceEnabled = 
    settingValue === 't' || 
    settingValue === true || 
    settingValue === 'true' ||
    (settingValue === undefined && !isLoading) ||
    (settingValue === null && !isLoading);

  const toggleIntelligence = async () => {
    try {
      const newValue = !intelligenceEnabled;
      
      await apiRequest('PUT', '/api/app-settings/salefishIntelligence', {
        settingValue: newValue,
      });
      
      // Invalidate queries immediately to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ['/api/app-settings'] });
      queryClient.invalidateQueries({ queryKey: ['/api/app-settings/salefishIntelligence'] });
    } catch (error) {
      console.error('Failed to toggle intelligence:', error);
    }
  };

  return (
    <IntelligenceContext.Provider value={{ intelligenceEnabled, isLoading, toggleIntelligence }}>
      {children}
    </IntelligenceContext.Provider>
  );
}

export function useIntelligence() {
  const context = useContext(IntelligenceContext);
  if (context === undefined) {
    throw new Error('useIntelligence must be used within an IntelligenceProvider');
  }
  return context;
}
